// Labor & Workforce System for Canntycoon

// ── Worker Generation ────────────────────────────────────────────────────────
const WORKER_TYPES = ['trimmer', 'grower', 'scientist', 'maintenance', 'manager'];
const WORKER_NAMES = [
  'Alex Chen', 'Morgan Lee', 'Jamie Torres', 'Casey Kim', 'Riley Patel',
  'Quinn Brooks', 'Avery Diaz', 'Jordan Walsh', 'Drew Nguyen', 'Blake Reyes',
  'Sam Foster', 'Taylor Hicks', 'Cameron West', 'Dakota Cruz', 'Skyler Moss',
];

let workerIdCounter = 1;

export function generateWorker(type = null) {
  const workerType = type || WORKER_TYPES[Math.floor(Math.random() * 3)]; // bias toward common types
  const name = WORKER_NAMES[Math.floor(Math.random() * WORKER_NAMES.length)];

  const baseSkill = workerType === 'grower' ? 45 : workerType === 'scientist' ? 60 : 30;
  const skill     = Math.min(100, baseSkill + Math.floor(Math.random() * 40));
  const reliability = 40 + Math.floor(Math.random() * 50);
  const speed       = 40 + Math.floor(Math.random() * 50);

  // Wage demand scales with skill — kept accessible for early-stage hiring
  const baseWage = { trimmer: 200, grower: 300, scientist: 450, maintenance: 250, manager: 400 };
  const wageDemand = Math.round((baseWage[workerType] || 250) * (0.8 + skill * 0.005));

  const isSaboteur = Math.random() < 0.02; // 2% hidden flag

  return {
    id: `worker-${workerIdCounter++}`,
    name,
    type: workerType,
    skill,
    reliability,
    speed,
    morale: 70 + Math.floor(Math.random() * 20),
    mistakeProbability: Math.max(0.01, 0.15 - skill * 0.001),
    wageDemand,
    currentWage: wageDemand,
    loyaltyScore: 40 + Math.floor(Math.random() * 40),
    isSaboteur,
    assignedRoomId: null,
    hiredOnDay: 0,
  };
}

// ── Refresh job market (called weekly) ───────────────────────────────────────
export function refreshJobMarket(stage) {
  const count = stage === 1 ? 2 : stage === 2 ? 4 : 6;
  const candidates = [];
  for (let i = 0; i < count; i++) {
    // Stage 1: only trimmers and growers; Stage 2+: scientists and managers
    const types = stage >= 2
      ? WORKER_TYPES
      : ['trimmer', 'grower', 'trimmer', 'grower']; // weighted toward basics
    const type = types[Math.floor(Math.random() * types.length)];
    candidates.push(generateWorker(type));
  }
  return candidates;
}

// ── Task Unit Requirements per Room per Tick ─────────────────────────────────
// Returns the number of "task units" required this tick
export function calculateRoomLaborRequirement(room, automationReduction = 0) {
  if (!room.batch) return 0.5; // idle room needs minimal check

  const baseByStage = {
    seedling:   1.0,
    vegetative: 2.0,
    flowering:  2.5,
    harvest:   12.0,  // surge labor needed
    curing:     0.5,
  };

  const base = baseByStage[room.batch.stage] || 1.0;
  // Sqrt scaling: 1 worker handles ~45 plants fine, 50+ causes minor stress
  const plantMultiplier = 1 + Math.sqrt(room.batch.plantCount - 1) * 0.45;
  const automatedPortion = base * automationReduction;
  return Math.max(0.1, (base - automatedPortion) * plantMultiplier / 24); // per tick (1/24 day)
}

// ── Task Units Produced by a Worker ─────────────────────────────────────────
export function calculateWorkerOutput(worker) {
  if (!worker) return 0;
  const moraleMultiplier = worker.morale / 100;
  return (worker.skill * 0.005 + worker.speed * 0.003) * moraleMultiplier;
}

// ── Apply Understaffing Penalty to Batch ─────────────────────────────────────
export function applyUnderstaffingPenalty(batch, deficitRatio) {
  if (!batch || deficitRatio <= 0) return batch;
  const updated = { ...batch };
  // Health penalty for neglect
  updated.health = Math.max(0, updated.health - deficitRatio * 0.3);
  // Pest detection lag increases
  if (batch.stage === 'flowering' || batch.stage === 'vegetative') {
    updated.moldRisk = Math.min(100, updated.moldRisk + deficitRatio * 0.2);
  }
  // Harvest stage is especially punishing
  if (batch.stage === 'harvest') {
    updated.yieldPotential = Math.max(0, updated.yieldPotential - deficitRatio * 1.5);
    updated.visualAppeal   = Math.max(0, updated.visualAppeal - deficitRatio * 0.8);
  }
  return updated;
}

// ── Morale System ─────────────────────────────────────────────────────────────
export function updateWorkerMorale(worker, { underpaidRatio, massFireEvent, lateWages }) {
  let moraleChange = 0;
  if (underpaidRatio > 0.1) moraleChange -= underpaidRatio * 10;   // underpaid
  if (massFireEvent)        moraleChange -= 8;                      // colleague fired
  if (lateWages)            moraleChange -= 15;                     // wages not paid
  moraleChange += 0.5;  // small passive recovery
  return {
    ...worker,
    morale: Math.max(0, Math.min(100, worker.morale + moraleChange)),
    loyaltyScore: Math.max(0, Math.min(100, worker.loyaltyScore + moraleChange * 0.5)),
  };
}

// ── Theft Risk Check ─────────────────────────────────────────────────────────
export function checkTheftRisk(worker, securityLevel) {
  if (!worker) return null;
  const moraleFactor = worker.morale < 40 ? 1.5 : 1.0;
  const securityFactor = 1 - (securityLevel / 5) * 0.8;
  const saboteurFactor = worker.isSaboteur ? 3.0 : 1.0;
  const prob = (1 - worker.loyaltyScore / 100) * securityFactor * moraleFactor * saboteurFactor * 0.0005;
  if (Math.random() < prob) {
    return {
      type: 'theft',
      workerId: worker.id,
      workerName: worker.name,
      cashLost: Math.round(200 + Math.random() * 800),
    };
  }
  return null;
}

// ── Weekly Wages Processing ──────────────────────────────────────────────────
export function processWages(workers, cash) {
  const totalWages = workers.reduce((sum, w) => sum + w.currentWage, 0);
  const canAfford = cash >= totalWages;
  const updatedWorkers = workers.map(worker => {
    return updateWorkerMorale(worker, { lateWages: !canAfford, massFireEvent: false, underpaidRatio: 0 });
  });
  return {
    updatedWorkers,
    wageCost: totalWages,
    canAfford,
  };
}

// ── Labor Coverage Report (facility-wide pooling) ────────────────────────────
// Workers serve the whole facility — total output is pooled against total need.
// A single skilled grower can cover two small rooms without dedicated assignment.
// Deficit is applied uniformly across all batched rooms.
export function getLaborReport(rooms, workers, unlockedTech) {
  const hasAutoBasic = unlockedTech.includes('A1');
  const hasAutoFull  = unlockedTech.includes('A3');
  const automationReduction = hasAutoFull ? 0.60 : hasAutoBasic ? 0.25 : 0;

  const batchedRooms = rooms.filter(r => r.unlocked && r.batch);
  const totalRequired = batchedRooms.reduce(
    (sum, r) => sum + calculateRoomLaborRequirement(r, automationReduction), 0
  );
  const totalProduced = workers.reduce((sum, w) => sum + calculateWorkerOutput(w), 0);
  const facilityCoverage = totalRequired > 0 ? Math.min(1, totalProduced / totalRequired) : 1;
  const facilityDeficit  = Math.max(0, 1 - facilityCoverage);

  return rooms.map(room => {
    const required = calculateRoomLaborRequirement(room, automationReduction);
    return {
      roomId:   room.id,
      required,
      produced: totalProduced,   // informational — full facility output
      coverage: facilityCoverage,
      deficit:  facilityDeficit,
    };
  });
}

// ── Research Points Generation ────────────────────────────────────────────────
export function calculateWeeklyRP(workers, labRoom) {
  const scientistRP = workers
    .filter(w => w.type === 'scientist' && w.assignedRoomId === 'room-lab')
    .reduce((sum, w) => {
      const base = 10 + (w.skill / 100) * 15;
      return sum + Math.round(base * (w.morale / 100));
    }, 0);

  if (scientistRP === 0) return 0; // equipment does nothing without scientists
  const equip = labRoom?.equipment || {};
  const equipBonus = Object.values(equip).reduce((s, e) => s + (e?.installed ? (e.rpBonus || 0) : 0), 0);
  return scientistRP + equipBonus;
}
